import React, { createContext, useContext, useState, useEffect } from 'react';
import { CreditTransaction, StripePaymentIntent } from '@/types';
import { creditService } from '@/services/credits';
import { useAuth } from './AuthContext';

interface CreditContextType {
  transactions: CreditTransaction[];
  loading: boolean;
  refreshTransactions: () => Promise<void>;
  purchaseCredits: (amount: number) => Promise<StripePaymentIntent | null>;
  confirmPayment: (paymentIntentId: string) => Promise<boolean>;
}

const CreditContext = createContext<CreditContextType | undefined>(undefined);

export function CreditProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, refreshUser } = useAuth();
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated && user) {
      refreshTransactions();
    }
  }, [isAuthenticated, user]);

  const refreshTransactions = async () => {
    if (!isAuthenticated) return;
    
    try {
      setLoading(true);
      const response = await creditService.getTransactions();
      if (response.success && response.data) {
        setTransactions(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const purchaseCredits = async (amount: number): Promise<StripePaymentIntent | null> => {
    try {
      setLoading(true);
      const response = await creditService.createPaymentIntent(amount);
      if (response.success && response.data) {
        return response.data;
      }
      return null;
    } catch (error) {
      console.error('Failed to create payment intent:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const confirmPayment = async (paymentIntentId: string): Promise<boolean> => {
    try {
      setLoading(true);
      const response = await creditService.confirmPayment(paymentIntentId);
      if (response.success) {
        await refreshUser(); // Refresh user credits
        await refreshTransactions(); // Refresh transaction history
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to confirm payment:', error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  return (
    <CreditContext.Provider value={{
      transactions,
      loading,
      refreshTransactions,
      purchaseCredits,
      confirmPayment
    }}>
      {children}
    </CreditContext.Provider>
  );
}

export function useCredits() {
  const context = useContext(CreditContext);
  if (context === undefined) {
    throw new Error('useCredits must be used within a CreditProvider');
  }
  return context;
}